import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@coin/shared/core/util-environments';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { MassReqResponse, PaginatedResult, TransactionStatus } from '@coin/shared/core/util-models';
import { LetterState, LetterType } from '@coin/shared/core/util-enums';
import { LoadingService } from '@coin/shared/core/data-access';
import { Season } from '@coin/shared/season-mgmt/util';
import { IncentiveRecordUpdate, SeasonRecord } from '@coin/shared/incentive/util';
import {
  IncentiveIppRecordAssignment,
  IncentiveIppRecordAssignmentUploadDto
} from '../components/incentive/incentive-monitoring/records/records/models/incentive-ipp-record-assignment.model';
import { RecordsService } from '../interfaces/record-service.interface';
import { IndividualMultiplierUpdateDto } from '../models/individual-multiplier-update.model';
import { SeasonState } from '../store';

@Injectable({
  providedIn: 'root'
})
export class IncentiveRecordsService implements RecordsService {
  private readonly httpClient = inject(HttpClient);
  private readonly loading = inject(LoadingService);
  private readonly toast = inject(ToastrService);
  private readonly seasonState = inject(SeasonState);

  queryPagedAndFilteredData = this.getRecordsPaginated;
  private _seasonId: string;

  set seasonId(id: string) {
    this._seasonId = id;
  }

  get seasonId(): string {
    return this._seasonId;
  }

  public getRecordsPaginated(
    page: number,
    size: number,
    searchText?: string,
    filterText?: string,
    sortText?: string
  ): Observable<PaginatedResult<SeasonRecord>> {
    return this.httpClient
      .get<
        PaginatedResult<SeasonRecord>
      >(`${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${this._seasonId}/records?Paging.Page=${page + 1}&Paging.Size=${size}${filterText}${sortText}`)
      .pipe(
        map(data => ({
          ...data,
          content: data.content.map(item => this.setLetterState(item, LetterType.TargetAgreementForm))
        })),
        catchError(() => of(null)) // do not cancel subscription in simple table component
      );
  }

  public getAvailableRecords(page: number, size: number, filter: string): Observable<PaginatedResult<string>> {
    return this.httpClient
      .get<
        PaginatedResult<string>
      >(`${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${this.seasonId}/records?Paging.Page=${page + 1}&Paging.Size=${size}&Query.isDesignReady=true&Query.State=Active&Query.State=Outdated${filter}`)
      .pipe(catchError(() => of(null)));
  }

  public getRecordById(recordId: string, letterType: LetterType): Observable<SeasonRecord> {
    return this.httpClient
      .get<SeasonRecord>(`${environment.api.baseUrl}/admin/api/v1/incentive/records/${recordId}`)
      .pipe(
        map(record => this.setLetterState(record, letterType)),
        catchError(() => of(null))
      );
  }

  public setLetterState(record: SeasonRecord, letterType: LetterType): SeasonRecord {
    return {
      ...record,
      recordCommunicationState:
        record?.recordLetterStates?.find(letter => letter?.letterType === letterType)?.state ?? LetterState.None
    };
  }

  public downloadIppRecords(seasonId: string): Observable<IncentiveIppRecordAssignment[]> {
    this.loading.present();
    return this.httpClient
      .get<
        IncentiveIppRecordAssignment[]
      >(`${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${seasonId}/records/ipp-download`)
      .pipe(
        tap(() => this.toast.success(`Downloaded IPP Assignments.`)),
        map(result => result.map(item => new IncentiveIppRecordAssignment(item))),
        finalize(() => this.loading.dismiss()),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(
            `Error trying to download ipp assignments: ${res.error?.title ? res.error?.title : res.error}`
          );
          return throwError(res.error);
        })
      );
  }

  public uploadIppRecords(body: IncentiveIppRecordAssignmentUploadDto, seasonId: string): Observable<void> {
    this.loading.present();
    return this.httpClient
      .patch<void>(
        `${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${seasonId}/records/ipp-upload`,
        body
      )
      .pipe(
        tap(() => this.toast.success(`Uploaded Records.`)),
        finalize(() => this.loading.dismiss()),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error trying to upload records: ${res.error?.title ? res.error?.title : res.error}`);
          return throwError(res.error);
        })
      );
  }

  public updateIndividualMultiplier(body: IndividualMultiplierUpdateDto, recordId: string): Observable<void> {
    return this.loading.withLoadingScreen(
      this.httpClient
        .patch<void>(
          `${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${body.seasonId}/records/${recordId}/individual-multiplier-value`,
          body
        )
        .pipe(
          tap(() => this.toast.success(`Updated Record.`)),
          catchError((res: HttpErrorResponse) => {
            this.toast.error(`Error trying to update record: ${res.error?.title ? res.error?.title : res.error}`);
            return throwError(res.error);
          })
        )
    );
  }

  public updateRecord(body: Partial<IncentiveRecordUpdate>, recordId: string): Observable<void> {
    this.loading.present();
    return this.httpClient
      .patch<void>(
        `${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${this.seasonState.currentSeason()?.id}/records/${recordId}`,
        body
      )
      .pipe(
        tap(() => this.toast.success(`Updated Record.`)),
        finalize(() => this.loading.dismiss()),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error trying to update record: ${res.error?.title ? res.error?.title : res.error}`);
          return throwError(res.error);
        })
      );
  }

  public updateRecords(
    seasonId: string,
    recordIds: string[],
    properties: IncentiveRecordUpdate,
    filterText?: string
  ): Observable<MassReqResponse> {
    this.loading.present();
    return this.httpClient
      .patch<MassReqResponse>(
        `${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${seasonId}/records${filterText ? `?${filterText}` : ''}`,
        {
          recordIds,
          properties
        }
      )
      .pipe(
        finalize(() => this.loading.dismiss()),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error trying to update records: ${res.error?.title ? res.error?.title : res.error}`);
          return throwError(res.error);
        })
      );
  }

  public getFieldValues(
    page: number,
    size: number,
    targetProperty: string,
    seasonId: string,
    searchText?: string
  ): Observable<PaginatedResult<string>> {
    const searchQuery = searchText.trim() ? `&${targetProperty}=${searchText}` : '';

    return this.httpClient
      .get<
        PaginatedResult<string>
      >(`${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${seasonId}/records/field-values?Page=${page + 1}&Size=${size}&targetProperty=${targetProperty}${searchQuery}`)
      .pipe(catchError(() => of(null)));
  }

  public recalculateProration(record: SeasonRecord, season: Season): Observable<TransactionStatus> {
    return this.httpClient
      .put<TransactionStatus>(
        `${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${season.id}/records/${record.id}/proration`,
        {}
      )
      .pipe(
        this.loading.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(
            `Error trying to recalculate record proration: ${res.error?.title ? res.error?.title : res.error}`
          );
          return throwError(res.error);
        })
      );
  }
}
